import { CON_CAMBIOS, ERROR, GUARDADO, GUARDANDO, SIN_CAMBIOS } from 'assets/states/savestates';
import { Modal, message } from 'antd';
import { usePAppApi, useUAppApi } from 'hooks/useAppApi';
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { saveDraft, loadDraft, clearDraft, hasDraft } from 'utils/offlineMantencionStore';
import { flushPendingDrafts } from 'services/offlineMantencionSync';
import { useOnlineStatus } from 'hooks/useOnlineStatus';
import useMantencionConfig from 'hooks/useMantencionConfig';
import {
  addHallazgo as addHallazgoPure,
  updateHallazgo as updateHallazgoPure,
  removeHallazgo as removeHallazgoPure,
  addHallazgoArrastrado as addHallazgoArrastradoPure,
  buildItemObsPayload,
  mergeHallazgosResponse,
  getHallazgoId,
  applyHallazgosResponse,
  hydrateObsSugFromActividades,
} from 'utils/multiHallazgo';
import {
  buildResolucionesPayload,
  buildResolucionesEliminadasPayload,
  DECISION,
} from 'utils/arrastrePrevios';

// Re-export de los helpers puros (multi-hallazgo) para back-compat con los
// imports existentes (`providers/MaintanceToDoProvider`). La lógica vive en
// `utils/multiHallazgo` y está testeada ahí.
export {
  buildItemObsPayload,
  mergeHallazgosResponse,
  getHallazgoId,
  applyHallazgosResponse,
} from 'utils/multiHallazgo';

// Componente proveedor del contexto
const MaintanceToDoContext = createContext(null);

// Lógica pura: lista los idRes de actividades VISIBLES que no tienen estado
// elegido (null/''), aplicando el override local de `estados` sobre el estado
// del fetch. El estado nace null; el técnico debe marcarlo (Exitoso / Con
// problema / No aplica) antes de finalizar. Los ítems no visibles (sin fecha en
// el mes) se ignoran porque no son operables en la UI.
export function findItemsVisiblesSinEstado(flatActividades, estados) {
  if (!flatActividades || flatActividades.length === 0) return [];
  const missing = [];
  for (const actividad of flatActividades) {
    if (!actividad.visible) continue;
    const idRes = actividad.id;
    const override = estados?.[idRes];
    const estadoEff = override !== undefined ? override : actividad.estado;
    if (!estadoEff || String(estadoEff).trim() === '') missing.push(Number(idRes));
  }
  return missing;
}

// Componente proveedor del contexto
export const MaintanceToDoProvider = ({ children }) => {
  // Almacenar estado de los items de mantencion
  const [estados, setEstados] = useState(null);
  // Almacenar los problemas que han tenido los items de mantencion
  const [problems, setProblems] = useState(null);
  // Almacenar las observaciones y sugerencias de mantención
  const [obsSug, setObsSug] = useState(null);
  // Decisiones del técnico sobre los hallazgos de visitas anteriores:
  // { [idHallazgoPrevio]: 'RESUELTO' | 'SIGUE' }. No son obligatorias: lo que no
  // se decide queda pendiente y reaparece en la próxima visita.
  const [decisionesPrevios, setDecisionesPrevios] = useState({});
  // IDs de hallazgos previos cuya decisión (RESUELTO o SIGUE) el técnico
  // deshizo después de haberla tomado. Viaja en el PUT como
  // `resolucionesEliminadas` (ver comentario en utils/arrastrePrevios.js).
  // Se acumula (nunca se saca un id) porque el backend es idempotente: mandar
  // un id sin decisión guardada no hace nada, así que reenviarlo en cada save
  // es más simple y robusto que rastrear si esa reversión concreta llegó a
  // persistirse.
  const [decisionesEliminadas, setDecisionesEliminadas] = useState([]);
  // Lista plana de actividades cargadas de backend (para validar severidades
  // al finalizar, incluso las que el usuario no tocó en esta sesión).
  const [flatActividades, setFlatActividades] = useState(null);
  // Almacenar las observaciones de mantención
  const [observaciones, setObservaciones] = useState(null);
  // Almacenar el estado de guardado
  const [saveStatus, setSaveStatus] = useState(SIN_CAMBIOS); // Iniciar con SIN_CAMBIOS
  const [ascensoresEstados, setAscensoresEstados] = useState(null);
  const [finishForm, setFinishForm] = useState();
  const [isLoading, setIsLoading] = useState(false);
  // Map de clave compuesta `${id_act_res}:${tipo}` → idHallazgo.
  // Task 11: clave compuesta para que INCIDENCIA y SUGERENCIA del mismo idActRes
  // coexistan sin pisarse. Se mergeea (no reemplaza) en cada save OK.
  const [hallazgosPorActRes, setHallazgosPorActRes] = useState(() => new Map());
  // saveTick: contador que se incrementa en cada save OK. Los items lo escuchan
  // en un useEffect para detectar "hubo un guardado" y disparar el aviso reactivo.
  const [saveTick, setSaveTick] = useState(0);
  // ID de la mantención actualmente abierta; necesario para key-ear el draft offline.
  const [mantencionId, setMantencionId] = useState(null);

  const isOnline = useOnlineStatus();
  const mantCfg = useMantencionConfig();

  const handleObservaciones = (data) => {
    setObservaciones(data);
    setToConCambios();
  };

  const handleAscensoresEstados = (data) => {
    setAscensoresEstados(data);
    setToConCambios();
  };

  const setToConCambios = () => {
    if (saveStatus !== CON_CAMBIOS) {
      setSaveStatus(CON_CAMBIOS);
    }
  };

  const onChangeEstado = (idRes, estado) => {
    setToConCambios();
    setEstados({
      ...estados,
      [idRes]: estado,
    });
  };

  const onChangeProblems = (idRes, data) => {
    setToConCambios();
    setProblems({
      ...problems,
      [idRes]: [
        ...data.map((item) => ({
          ...item,
          image: item?.image?.length > 0 ? item?.image[0].response?.filename : null,
        })),
      ],
    });
  };

  // Acciones multi-hallazgo. Cada ítem es una lista de incidencias + sugerencias.
  // addHallazgo genera el tempId (identidad cliente) y lo devuelve para keyear
  // las fotos de la fila nueva. update/remove operan por tempId.
  const addHallazgo = useCallback((idRes, tipo) => {
    let nuevoTempId = null;
    setObsSug((prev) => {
      const { obsSug: next, tempId } = addHallazgoPure(prev, idRes, tipo);
      nuevoTempId = tempId;
      return next;
    });
    setToConCambios();
    return nuevoTempId;
  }, []);

  const updateHallazgo = useCallback((idRes, tipo, tempId, patch) => {
    setObsSug((prev) => updateHallazgoPure(prev, idRes, tipo, tempId, patch));
    setToConCambios();
  }, []);

  const removeHallazgo = useCallback((idRes, tipo, tempId) => {
    setObsSug((prev) => removeHallazgoPure(prev, idRes, tipo, tempId));
    setToConCambios();
  }, []);

  // Ref espejo de `obsSug`: `decidirPrevio` necesita leer, de forma síncrona y
  // ANTES de despachar ningún `setState`, si el previo ya tiene una incidencia
  // arrastrada sembrada en el ítem (camino idempotente). No se puede leer
  // `obsSug` directamente porque `decidirPrevio` es un `useCallback([])` — agregar
  // `obsSug` a las deps rompería su identidad estable entre renders. Se
  // mantiene con un `useEffect`, mismo patrón que `pendingRef` en HallazgoRow.
  const obsSugRef = useRef(obsSug);
  useEffect(() => {
    obsSugRef.current = obsSug;
  }, [obsSug]);

  // Ref espejo de `decisionesPrevios`, mismo motivo que `obsSugRef`:
  // `limpiarArrastresDelItem` es un `useCallback([])` y necesita leer, de
  // forma síncrona, qué previos tienen HOY una decisión SIGUE (para saber
  // cuáles de verdad va a revertir) sin volverse inestable entre renders.
  const decisionesPreviosRef = useRef(decisionesPrevios);
  useEffect(() => {
    decisionesPreviosRef.current = decisionesPrevios;
  }, [decisionesPrevios]);

  // Registra que la decisión de `idHallazgoPrevio` se deshizo. Ver comentario
  // en el `useState` de `decisionesEliminadas`: se acumula, nunca se saca.
  const registrarReversion = useCallback((idHallazgoPrevio) => {
    setDecisionesEliminadas((prev) =>
      prev.includes(idHallazgoPrevio) ? prev : [...prev, idHallazgoPrevio]
    );
  }, []);

  // "Resuelto": sólo registra la decisión (viaja en resolucionesHallazgos). Si
  // el ítem ya tenía una incidencia arrastrada sembrada por un SIGUE anterior
  // sobre el mismo previo, se retira (mismo camino que `deshacerDecisionPrevio`:
  // si ya estaba persistida, su `idHallazgo` queda en `eliminados`). Sin esto,
  // cambiar de opinión de SIGUE a RESUELTO mandaría el mismo hallazgo previo
  // por los dos caminos y el backend crearía dos hallazgos por el arrastre.
  // Además, si al retirarla el ítem queda sin ninguna incidencia, el "Con
  // problema" que puso el SIGUE anterior ya no tiene sustento: el ítem vuelve a
  // quedar sin estado (null), simétrico a como quedó marcado. No se toca si
  // queda otra incidencia (propia o de otro previo) ni si el estado no era
  // RECHAZADO (lo eligió el técnico a mano por otro motivo) — así no se pisa
  // una elección manual.
  // "Sigue presente": siembra la incidencia arrastrada (precargada y editable) y
  // pasa el ítem a "Con problema" — si algo sigue pendiente, el ítem tiene un
  // problema, y además la lista de incidencias sólo se muestra en ese estado.
  // Idempotente: si el previo ya tiene una incidencia sembrada en el ítem, no
  // crea una segunda (evita duplicar el arrastre ante un doble tap) y devuelve
  // el `tempId` de la fila ya sembrada (no uno nuevo, que quedaría huérfano:
  // no correspondería a ninguna fila real de `obsSug`).
  //
  // Nota: el ítem pasa a RECHAZADO con `setEstados` funcional (no llamando a
  // `onChangeEstado`, que no es estable entre renders) para no perder, por el
  // closure obsoleto de un `useCallback([])`, los demás estados ya seteados.
  const decidirPrevio = useCallback(
    (idRes, previo, accion) => {
      const previoId = previo.id;
      const existente = (obsSugRef.current?.[idRes]?.incidencias || []).find(
        (h) => h.idHallazgoPrevio === previoId
      );
      const tempId = accion === DECISION.SIGUE ? existente?.tempId ?? uuidv4() : null;

      setObsSug((prev) => {
        const item = prev?.[idRes];
        const existenteEnPrev = (item?.incidencias || []).find(
          (h) => h.idHallazgoPrevio === previoId
        );

        if (accion === DECISION.SIGUE) {
          if (existenteEnPrev) return prev; // ya estaba sembrada: no duplicar
          return addHallazgoArrastradoPure(prev, idRes, previo, tempId).obsSug;
        }
        if (!existenteEnPrev) return prev;
        return removeHallazgoPure(prev, idRes, 'INCIDENCIA', existenteEnPrev.tempId);
      });

      setDecisionesPrevios((prev) => ({ ...prev, [previoId]: accion }));

      if (accion === DECISION.SIGUE) {
        setEstados((prev) => ({ ...prev, [idRes]: 'RECHAZADO' }));
      } else if (existente) {
        // Cambio de decisión sin pasar por "Deshacer": había una incidencia
        // arrastrada sembrada (SIGUE) y ahora se decide RESUELTO. Esa SIGUE
        // pudo haber quedado guardada en una visita previa — hay que avisarle
        // al backend que la revierta (resolucionesEliminadas) antes de aplicar
        // la nueva decisión (resolucionesHallazgos).
        registrarReversion(previoId);
        setEstados((prev) => {
          if (prev?.[idRes] !== 'RECHAZADO') return prev;
          const quedanIncidencias = (obsSugRef.current?.[idRes]?.incidencias || []).some(
            (h) => h.idHallazgoPrevio !== previoId
          );
          if (quedanIncidencias) return prev;
          return { ...prev, [idRes]: null };
        });
      }
      setToConCambios();
      return tempId;
    },
    [registrarReversion]
  );

  // El tempId de la incidencia arrastrada no se guarda en un mapa paralelo: se
  // deriva de `obsSug` (buscando la incidencia del ítem con `idHallazgoPrevio`
  // igual al previo), que es la única fuente de verdad y la que sí se persiste
  // en el draft offline. Un mapa paralelo (`tempIdPorPrevio`) quedaría
  // desincronizado tras recargar un draft, porque `bindMantencion` lo resetea
  // mientras que `obsSug` se hidrata desde el draft.
  const deshacerDecisionPrevio = useCallback(
    (idRes, idHallazgoPrevio) => {
      // La decisión pudo haberse guardado en un save anterior (online u
      // offline): avisar al backend que la revierta.
      registrarReversion(idHallazgoPrevio);

      // Antes de tocar obsSug: ¿había una incidencia arrastrada sembrada por
      // un SIGUE (para saber si, al retirarla, hay que soltar el "Con
      // problema" que puso esa misma decisión)? Mismo patrón que `existente`
      // en `decidirPrevio` — leído del ref, no del state, porque este
      // callback es estable ([]).
      const existente = (obsSugRef.current?.[idRes]?.incidencias || []).find(
        (h) => h.idHallazgoPrevio === idHallazgoPrevio
      );

      setDecisionesPrevios((prev) => {
        const next = { ...prev };
        delete next[idHallazgoPrevio];
        return next;
      });
      setObsSug((prev) => {
        const item = prev?.[idRes];
        const existenteEnPrev = (item?.incidencias || []).find(
          (h) => h.idHallazgoPrevio === idHallazgoPrevio
        );
        if (!existenteEnPrev) return prev;
        return removeHallazgoPure(prev, idRes, 'INCIDENCIA', existenteEnPrev.tempId);
      });

      // Si la incidencia retirada era la única que sostenía el "Con
      // problema" (lo puso el SIGUE, no el técnico a mano), el ítem vuelve a
      // quedar sin estado — mismo criterio que decidirPrevio('RESUELTO').
      if (existente) {
        setEstados((prev) => {
          if (prev?.[idRes] !== 'RECHAZADO') return prev;
          const quedanIncidencias = (obsSugRef.current?.[idRes]?.incidencias || []).some(
            (h) => h.idHallazgoPrevio !== idHallazgoPrevio
          );
          if (quedanIncidencias) return prev;
          return { ...prev, [idRes]: null };
        });
      }
      setToConCambios();
    },
    [registrarReversion]
  );

  // El técnico reclasificó el ítem a Exitoso / No aplica: los arrastres de ese
  // ítem se deshacen (el hallazgo previo vuelve a quedar sin confirmar).
  const limpiarArrastresDelItem = useCallback(
    (idRes, previosDelItem) => {
      // Solo los SIGUE de este ítem son arrastres reales (el RESUELTO no
      // sembró ninguna incidencia y se deja como estaba). Se calcula ANTES de
      // disparar los setState de abajo, leyendo el ref (mismo motivo que en
      // `deshacerDecisionPrevio`).
      const previosSigue = (previosDelItem || []).filter(
        (p) => decisionesPreviosRef.current[p.id] === DECISION.SIGUE
      );
      previosSigue.forEach((p) => registrarReversion(p.id));

      (previosDelItem || []).forEach((p) => {
        setDecisionesPrevios((prev) => {
          if (prev[p.id] !== DECISION.SIGUE) return prev;
          const next = { ...prev };
          delete next[p.id];
          return next;
        });
        setObsSug((prev) => {
          const item = prev?.[idRes];
          const existente = (item?.incidencias || []).find((h) => h.idHallazgoPrevio === p.id);
          if (!existente) return prev;
          return removeHallazgoPure(prev, idRes, 'INCIDENCIA', existente.tempId);
        });
      });

      // Si al retirar los arrastres el ítem queda sin ninguna incidencia, el
      // "Con problema" que pusieron esos SIGUE ya no tiene sustento: vuelve a
      // quedar sin estado. No se pisa si queda otra incidencia (propia o de
      // otro previo) — ahí el técnico (o un previo aún activo) sostiene el
      // estado.
      if (previosSigue.length > 0) {
        const idsRevertidos = new Set(previosSigue.map((p) => p.id));
        setEstados((prev) => {
          if (prev?.[idRes] !== 'RECHAZADO') return prev;
          const quedanIncidencias = (obsSugRef.current?.[idRes]?.incidencias || []).some(
            (h) => !idsRevertidos.has(h.idHallazgoPrevio)
          );
          if (quedanIncidencias) return prev;
          return { ...prev, [idRes]: null };
        });
      }
      setToConCambios();
    },
    [registrarReversion]
  );

  // La página registra acá su setter de ascensor activo. El checklist sólo
  // renderiza el ascensor de la pestaña activa, así que para poder "ir a
  // corregir" un ítem de OTRO ascensor primero hay que cambiar de pestaña.
  const ascensorNavigatorRef = useRef(null);
  const registerAscensorNavigator = useCallback((fn) => {
    ascensorNavigatorRef.current = fn;
  }, []);

  // Aplica los textos corregidos elegidos en la revisión IA. `general` es la
  // observación general de la mantención; el resto de las keys identifican un
  // hallazgo puntual con el formato "<idRes>:<tipo>:<tempId>" (multi-hallazgo).
  const aplicarTextosCorregidos = useCallback((finales) => {
    let nextObservaciones;
    const updates = [];
    Object.entries(finales || {}).forEach(([key, texto]) => {
      if (key === 'general') {
        nextObservaciones = texto;
        return;
      }
      const [idRes, tipo, tempId] = key.split(':');
      if (!idRes || !tipo || !tempId) return;
      updates.push({ idRes, tipo, tempId, texto });
    });
    if (updates.length > 0) {
      setObsSug((prev) => {
        let next = prev;
        for (const u of updates) {
          next = updateHallazgoPure(next, u.idRes, u.tipo, u.tempId, { descripcion: u.texto });
        }
        return next;
      });
    }
    if (nextObservaciones !== undefined) setObservaciones(nextObservaciones);
    if (updates.length > 0 || nextObservaciones !== undefined) setToConCambios();
  }, []);

  // La página llama bindMantencion(id) al cargar la mantención: fija el id para
  // el keying del draft y, si hay un draft local pendiente (sesión offline
  // previa), restaura el avance del técnico y lo marca como CON_CAMBIOS.
  const bindMantencion = useCallback((id) => {
    setMantencionId(id);
    const draft = loadDraft(id);
    // Siempre arrancar limpio para no filtrar estado de la mantención anterior.
    setEstados(null);
    setObsSug(null);
    setProblems(null);
    setObservaciones(null);
    setAscensoresEstados(null);
    setDecisionesPrevios({});
    setDecisionesEliminadas([]);
    setSaveStatus(SIN_CAMBIOS);
    if (!draft) return;
    if (draft.estados) setEstados(draft.estados);
    if (draft.obsSug) setObsSug(draft.obsSug);
    if (draft.problems) setProblems(draft.problems);
    if (draft.observaciones !== undefined) setObservaciones(draft.observaciones);
    if (draft.ascensoresEstados) setAscensoresEstados(draft.ascensoresEstados);
    if (draft.decisionesPrevios) setDecisionesPrevios(draft.decisionesPrevios);
    if (draft.decisionesEliminadas) setDecisionesEliminadas(draft.decisionesEliminadas);
    setSaveStatus(CON_CAMBIOS);
  }, []);

  // La página alimenta la lista plana de actividades del GET. Además de fijarla,
  // hidrata obsSug con los hallazgos persistidos (incidencias/sugerencias por
  // ítem) SOLO si aún no hay obsSug (no hay draft ni edits) — así al recargar una
  // mantención ya guardada el técnico ve sus hallazgos sin pisar avance local.
  const setFlatActividadesAndHydrate = useCallback((flat) => {
    setFlatActividades(flat);
    setObsSug((prev) => prev ?? hydrateObsSugFromActividades(flat));
  }, []);

  const hasUnsyncedChanges =
    mantencionId != null && (saveStatus === CON_CAMBIOS || saveStatus === ERROR);

  const { call: notFunctionalUpdateMant } = useUAppApi({
    url: '/mantenciones',
    mapResults: useCallback((data) => {
      return data;
    }, []),
  });

  const { call: postFinishMant } = usePAppApi({
    url: '/mantenciones/finish',
    mapResults: useCallback((data) => {
      return data;
    }, []),
  });

  const save = async (id) => {
    setSaveStatus(GUARDANDO);
    setIsLoading(true);

    try {
      let formatedProblems = {};
      if (problems) {
        for (let key in problems) {
          const items = problems[key];
          let result = [];
          items?.forEach((item) => {
            let image = item?.image;
            if (Array.isArray(image) && image?.length > 0) {
              result.push({
                ...item,
                image: image[0].existing ? image[0].url : image[0].response.path,
              });
            } else {
              result.push(item);
            }
          });
          formatedProblems[key] = result;
        }
      }

      const resoluciones = buildResolucionesPayload(decisionesPrevios);
      const eliminadas = buildResolucionesEliminadasPayload(decisionesEliminadas);
      const response = await notFunctionalUpdateMant(
        {
          items: estados,
          problems: formatedProblems,
          mantencionData: observaciones ? { observaciones } : null,
          itemObs: buildItemObsPayload(obsSug),
          ascensoresEstados: ascensoresEstados,
          ...(resoluciones.length > 0 ? { resolucionesHallazgos: resoluciones } : {}),
          ...(eliminadas.length > 0 ? { resolucionesEliminadas: eliminadas } : {}),
        },
        `/${id}`
      );

      if (!response) {
        // PUT sin data: fallo (mala/sin conexión o error del servidor). NO se
        // limpian estados/obsSug: quedan en memoria para reintentar al volver
        // el internet. El estado ERROR deja el botón actionable ("Reintentar").
        setSaveStatus(ERROR);
        message.error('No se pudo guardar. Revisá tu conexión y reintentá.');
        return false;
      }

      // Backfill: escribir el idHallazgo real en cada hallazgo de obsSug por su
      // tempId. Sin esto, un segundo save volvería a CREAR (el backend ya no
      // deduplica por tipo). También mergeamos el map tempId→idHallazgo (fotos).
      if (Array.isArray(response.hallazgos)) {
        setObsSug((prev) => applyHallazgosResponse(prev, response.hallazgos));
        setHallazgosPorActRes((prev) => mergeHallazgosResponse(prev, response.hallazgos));
      }

      setSaveTick((t) => t + 1);
      // Reflejar el estado guardado en la lista plana (para la validación de
      // cierre, que lee estado por ítem). Las listas de hallazgos son source of
      // truth en obsSug — no se copian a flatActividades.
      setFlatActividades((prev) => {
        if (!prev) return prev;
        return prev.map((a) => {
          const nextEstado = estados?.[a.id];
          return nextEstado !== undefined ? { ...a, estado: nextEstado } : a;
        });
      });
      setSaveStatus(GUARDADO);
      setSaveStatus(SIN_CAMBIOS);
      setProblems(null);
      return true;
    } catch (e) {
      // Excepción de red: nunca dejar el estado trabado en GUARDANDO.
      setSaveStatus(ERROR);
      message.error('No se pudo guardar. Revisá tu conexión y reintentá.');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Persistir el snapshot del checklist cuando hay cambios sin sincronizar;
  // limpiarlo cuando quedó sincronizado (SIN_CAMBIOS).
  useEffect(() => {
    if (mantencionId == null) return;
    if (saveStatus === CON_CAMBIOS || saveStatus === ERROR) {
      saveDraft(mantencionId, {
        estados,
        obsSug,
        problems,
        observaciones,
        ascensoresEstados,
        decisionesPrevios,
        decisionesEliminadas,
      });
    } else if (saveStatus === SIN_CAMBIOS) {
      clearDraft(mantencionId);
    }
  }, [
    estados,
    obsSug,
    problems,
    observaciones,
    ascensoresEstados,
    decisionesPrevios,
    decisionesEliminadas,
    saveStatus,
    mantencionId,
  ]);

  // Auto-sync al reconectar: en la transición offline→online, si hay cambios
  // pendientes para la mantención abierta, dispara save(). Lee save/saveStatus
  // frescos vía ref para no re-disparar por cambios de esas deps.
  const latestRef = useRef({ save: null, saveStatus, mantencionId });
  latestRef.current = { save, saveStatus, mantencionId };
  const wasOnlineRef = useRef(isOnline);
  useEffect(() => {
    const cameOnline = !wasOnlineRef.current && isOnline;
    wasOnlineRef.current = isOnline;
    if (!cameOnline) return;
    const { mantencionId: mid } = latestRef.current;
    // Autoridad única de sincronización: el daemon `flushPendingDrafts` sube
    // TODOS los drafts pendientes (checklist) + sus fotos desde IndexedDB. No
    // llamamos `save()` acá: hacerlo dispararía el segundo camino de subida de
    // fotos (uploadPending, al aparecer idHallazgo tras el merge) → doble subida.
    // Tras el flush, reconciliamos el estado de la mantención abierta: si su
    // draft ya se sincronizó (no queda en el store), marcamos SIN_CAMBIOS.
    (async () => {
      await flushPendingDrafts();
      if (mid != null && !hasDraft(mid)) {
        setSaveStatus(SIN_CAMBIOS);
      }
    })();
  }, [isOnline]);

  // Lista los idRes de actividades con observación no vacía pero sin severidad.
  // La severidad es obligatoria al FINALIZAR la mantención (no al guardar
  // intermedio), porque cada observación con texto genera un hallazgo tipo
  // INCIDENCIA en el backend y la severidad es requerida.
  //
  // Itera sobre flatActividades (cargado del backend) aplicando los overrides
  // locales del obsSug. Así detecta tanto observaciones viejas sin severidad
  // como las nuevas de esta sesión.
  // Reglas (multi-hallazgo): si una actividad quedó RECHAZADO, debe tener al
  // menos UNA incidencia con texto, y TODA incidencia con texto debe tener
  // severidad (espejo del gate backend `assertIncidenciasCompletas`). Los
  // estados EXITOSO y NA no requieren incidencias. Las sugerencias son libres.
  const validarSeveridades = () => {
    if (!flatActividades || flatActividades.length === 0) {
      return { valid: true, missingIds: [] };
    }
    const missing = [];
    for (const actividad of flatActividades) {
      const idRes = actividad.id;
      const stateOverride = estados?.[idRes];
      const estadoEff = stateOverride !== undefined ? stateOverride : actividad.estado;
      if (estadoEff !== 'RECHAZADO') continue;
      const item = obsSug?.[idRes];
      const incidencias = item?.incidencias || [];
      const conTexto = incidencias.filter(
        (h) => h.descripcion && String(h.descripcion).trim() !== ''
      );
      // RECHAZADO sin ninguna incidencia con texto → falta describir el problema.
      if (conTexto.length === 0) {
        missing.push(Number(idRes));
        continue;
      }
      // Alguna incidencia con texto sin severidad → incompleta (solo si se exige).
      const exigeSeveridad = mantCfg.severidad && mantCfg.severidadObligatoria;
      if (exigeSeveridad && conTexto.some((h) => !h.severidad)) missing.push(Number(idRes));
    }
    return { valid: missing.length === 0, missingIds: missing };
  };

  // Helper expuesto: valida y si hay faltantes abre el Modal con scroll.
  // Retorna true si se puede continuar (todas las observaciones tienen severidad).
  // Se usa desde openFinish ANTES del save, porque save() limpia obsSug
  // y después la validación no tiene cómo detectar los cambios de sesión.
  const validarYNotificarSeveridades = () => {
    const { valid, missingIds } = validarSeveridades();
    if (valid) return true;
    Modal.error({
      title: 'Faltan datos en actividades con problema',
      content: (
        <div>
          <p style={{ marginBottom: 8 }}>
            No se puede finalizar la mantención: hay{' '}
            <strong>
              {missingIds.length} actividad
              {missingIds.length === 1 ? '' : 'es'} marcada
              {missingIds.length === 1 ? '' : 's'} como &quot;Con problema&quot;
            </strong>{' '}
            que todavía no tiene{missingIds.length === 1 ? '' : 'n'} observación o severidad
            completa.
          </p>
          <p style={{ marginBottom: 0, color: '#595959' }}>
            Al cerrar este aviso te voy a llevar a la primera actividad pendiente. Podés guardar el
            avance las veces que quieras sin finalizar.
          </p>
        </div>
      ),
      okText: 'Ir a corregir',
      onOk: () => scrollToActividad(missingIds[0]),
    });
    return false;
  };

  // Valida que toda actividad visible tenga estado marcado. Si faltan, abre un
  // Modal y lleva al primer pendiente. Espejo de validarYNotificarSeveridades.
  // Se usa en openFinish ANTES del save (el estado vive en `estados`/flat).
  // Retorna true si se puede continuar.
  const validarYNotificarEstados = () => {
    const missing = findItemsVisiblesSinEstado(flatActividades, estados);
    if (missing.length === 0) return true;
    Modal.error({
      title: 'Faltan ítems por marcar',
      content: (
        <div>
          <p style={{ marginBottom: 8 }}>
            No se puede finalizar la mantención: hay{' '}
            <strong>
              {missing.length} ítem{missing.length === 1 ? '' : 's'} sin estado
            </strong>
            . Marcá cada actividad como &quot;Exitoso&quot;, &quot;Con problema&quot; o &quot;No
            aplica&quot;.
          </p>
          <p style={{ marginBottom: 0, color: '#595959' }}>
            Al cerrar este aviso te voy a llevar al primer ítem pendiente. Podés guardar el avance
            las veces que quieras sin finalizar.
          </p>
        </div>
      ),
      okText: 'Ir a marcar',
      onOk: () => scrollToActividad(missing[0]),
    });
    return false;
  };

  const scrollToActividad = (idRes) => {
    // Si el ítem pendiente está en otro ascensor, cambiar primero de pestaña:
    // el checklist sólo renderiza el ascensor activo, así que sin esto el nodo
    // no existe en el DOM y el "ir a corregir" no lleva a ningún lado.
    const target = flatActividades?.find((a) => Number(a.id) === Number(idRes));
    const navigate = ascensorNavigatorRef.current;
    const cambiaAscensor = target?.id_ascensor != null && typeof navigate === 'function';
    if (cambiaAscensor) navigate(target.id_ascensor);

    const doScroll = () => {
      const el = document.getElementById(`actividad-respuesta-${idRes}`);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Flash visual — ring rojo que se desvanece.
      const prevTransition = el.style.transition;
      const prevBoxShadow = el.style.boxShadow;
      el.style.transition = 'box-shadow 0.4s ease-in-out';
      el.style.boxShadow = '0 0 0 4px rgba(239, 68, 68, 0.7)';
      setTimeout(() => {
        el.style.boxShadow = prevBoxShadow;
        setTimeout(() => {
          el.style.transition = prevTransition;
        }, 500);
      }, 1800);
    };

    // Si cambiamos de ascensor, esperar el re-render de la pestaña antes de
    // buscar el nodo; si ya estábamos en el ascensor correcto, scrollear ya.
    if (cambiaAscensor) setTimeout(doScroll, 250);
    else doScroll();
  };

  // Corre las validaciones de cierre en orden y corta en la primera que falla
  // (esa ya mostró su propio Modal y navegó al pendiente). Los hallazgos previos
  // no tienen gate acá: `decidirPrevio`/`deshacerDecisionPrevio` ya mantienen
  // `decisionesPrevios` y la incidencia arrastrada de `obsSug` consistentes
  // entre sí en todo momento.
  const puedeCerrarOffline = () => {
    return validarYNotificarEstados() && validarYNotificarSeveridades();
  };

  const handleFinishForm = (values) => {
    // La validación de severidades ocurre en openFinish() ANTES del save,
    // con obsSug todavía poblado. En este punto obsSug ya fue limpiado y
    // flatActividades puede estar stale (no hay refetch post-save), así que
    // revalidar acá daría falsos negativos. Confiamos en openFinish.
    setFinishForm(values);
    return postFinishMant(values);
  };

  return (
    <MaintanceToDoContext.Provider
      value={{
        onChangeEstado,
        save,
        onChangeProblems,
        addHallazgo,
        updateHallazgo,
        removeHallazgo,
        decisionesPrevios,
        decidirPrevio,
        deshacerDecisionPrevio,
        limpiarArrastresDelItem,
        registerAscensorNavigator,
        aplicarTextosCorregidos,
        setFlatActividades: setFlatActividadesAndHydrate,
        validarYNotificarEstados,
        validarYNotificarSeveridades,
        puedeCerrarOffline,
        saveStatus,
        observaciones,
        handleObservaciones,
        ascensoresEstados,
        handleAscensoresEstados,
        finishForm,
        handleFinishForm,
        isLoading,
        estados,
        obsSug,
        problems,
        flatActividades,
        hallazgosPorActRes,
        getHallazgoId,
        saveTick,
        bindMantencion,
        mantencionId,
        isOnline,
        hasUnsyncedChanges,
      }}
    >
      {children}
    </MaintanceToDoContext.Provider>
  );
};

export const useMaintanceToDo = () => useContext(MaintanceToDoContext);
